<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcPropertyGeneralData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    property: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.property,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('property');
    }
  );

  const act_2095089031469157_emit_s82qwnmc0_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_ahb9jiwpn_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_wmbea2jv7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'PropertyEditor',
      props: {
        target: 'general',
        property: _.cloneDeep(props.property),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_wgobcwh20 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_sf3ho0plt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_wgobcwh20,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_sf3ho0plt],
    event: 'reload',
  });
  const act_2095089031469157_emit_s82qwnmc0 = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_s82qwnmc0_getActionArgs,
  });
  const inst_evh_2095089031469157_close_ahb9jiwpn =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_s82qwnmc0,
      event: 'close',
      when: inst_evh_2095089031469157_close_ahb9jiwpn_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_wmbea2jv7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_wmbea2jv7_getActionArgs,
      events: [inst_evh_2095089031469157_close_ahb9jiwpn],
    });
  const inst_evh_2095089031469157_clickPreventStop_qhvfezh9b =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_wmbea2jv7,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_qhvfezh9b],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_sf3ho0plt,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_ahb9jiwpn,
    inst_evh_2095089031469157_clickPreventStop_qhvfezh9b,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_sf3ho0plt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_sf3ho0plt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_ahb9jiwpn = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_ahb9jiwpn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_qhvfezh9b = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_qhvfezh9b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Property', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Property', 'Type')"
          :content="property.property_type"
          v-if="property.property_type"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Property', 'Owner')">
          <ItpLink
            :text="property.owner.name"
            :to="{
              name: 'app.company',
              params: { id: property.owner.id },
            }"
          >
          </ItpLink>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Property', 'Name')"
          :content="property.name"
          v-if="property.name"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Property', 'Code')"
          :content="property.code"
          v-if="property.code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Property', 'Description')"
          :content="property.description"
          v-if="property.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Property', 'Area')"
          v-if="property.area"
        >
          <ItpText> {{ property.area }} {{ property.unit_of_area }} </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Property', 'Location')"
          :content="property.location_code"
          v-if="property.location_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Property', 'Validity')">
          <UcPropertyValidityCard :item="property"> </UcPropertyValidityCard>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
